<template>
  <div>
    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        名称
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-row dense>
      <v-col cols="12">
        <text-field-like
            v-if="readonly"
            :value="calendarView.name"
            label="名称">
        </text-field-like>
        <v-text-field
            v-else
            v-model="calendarView.name"
            label="名称"
            :rules="[v => !!v || '名称が必要です']"
            autofocus>
        </v-text-field>
      </v-col>
    </v-row>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        説明
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-row dense>
      <v-col cols="12">
        <text-field-like
            v-if="readonly"
            :value="calendarView.description"
            label="説明"
            placeholder="未設定"
            multi-line>
        </text-field-like>
        <v-textarea
            v-else
            v-model="calendarView.description"
            label="説明"
            messages="複数行入力可"
            rows="1"
            auto-grow>
        </v-textarea>
      </v-col>
    </v-row>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        基準日時
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-row dense>
      <v-col cols="6" class="el-form-col">
        <text-field-like
            v-if="readonly"
            :value="formatDateTime(calendarView.startDateWithTZ)"
            label="基準日時">
        </text-field-like>
        <template v-else>
          <label class="v-label theme--light">基準日時</label>
          <el-date-picker
              v-model="calendarView.startDate"
              type="datetime"
              placeholder="未設定"
              :readonly="readonly"
              :clearable="false">
          </el-date-picker>
        </template>
      </v-col>
      <v-col cols="6">
        <text-field-like
            v-if="readonly"
            :value="timeZoneString"
            label="タイムゾーン"
            hide-details>
        </text-field-like>
        <v-autocomplete
            v-else
            v-model="calendarView.recurrenceTimeZone"
            :items="timeZoneItems"
            label="タイムゾーン"
            hide-details>
        </v-autocomplete>
      </v-col>
    </v-row>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        サマリ
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-row dense>
      <v-col cols="12">
        <text-field-like
            :value="calendarView.summary"
            label="サマリ"
            hide-details>
        </text-field-like>
      </v-col>
    </v-row>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      繰り返し
    - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->

    <v-row dense v-if="!readonly">
      <v-col cols="12" class="form-col">
        <v-checkbox
            v-model="calendarView.isRepeatable"
            label="繰り返す"
            hide-details>
        </v-checkbox>
      </v-col>
    </v-row>

    <template v-if="!readonly && calendarView.isRepeatable">
      <v-card outlined flat class="section">
        <v-card-title>繰り返し</v-card-title>
        <v-card-text>

          <!-- 間隔 -->
          <v-row dense>
            <v-col cols="2" class="label-col"><label>繰り返す間隔</label></v-col>
            <v-col cols="10" class="form-col">
              <v-text-field
                  v-model="calendarView.interval"
                  :rules="[
                    v => !!v || v === 0 || '数値を入力してください',
                    v => !!/^-?[0-9]+(\.[0-9]*)?$/.test(v) || '数値を入力してください',
                    v => !/\./.test(v) || '整数を入力してください',
                    v => v <= 10000 || '10000 以下の値を入力してください',
                    v => v >= 1 || '1 以上の値を入力してください'
                  ]"
                  type="number"
                  min="1"
                  max="10000"
                  placeholder="間隔"
                  dense>
                <template #append>
                  <v-select
                      v-model="calendarView.intervalType"
                      :items="intervalTypeItems"
                      style="width: 200px;"
                      hide-details
                      flat solo
                      dense>
                  </v-select>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- 曜日 -->
          <v-row dense v-if="['minute', 'hour', 'week'].includes(calendarView.intervalType)">
            <v-col cols="2" class="label-col"><label>曜日</label></v-col>
            <v-col cols="10" class="form-col">
              <v-btn
                  v-for="(text, key) in daysOfWeek"
                  :key="key"
                  @click="toggleDayOfWeek(key)"
                  :color="btnColorDayOfWeek(key)"
                  class="mr-2 elevation-0"
                  rounded small>
                {{ text }}
              </v-btn>
            </v-col>
          </v-row>

          <v-row dense v-if="calendarView.intervalType === 'month'">
            <v-col cols="2" class="label-col"><label>間隔</label></v-col>
            <v-col cols="10" class="form-col">
              <v-select
                  v-model="calendarView.intervalTypeDetail"
                  :items="intervalTypeDetailList">
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        終了
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <template v-if="!readonly && calendarView.isRepeatable">
      <v-card outlined flat class="section">
        <v-card-title>終了</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="2">
              <v-radio-group
                  v-model="calendarView.endType"
                  row
                  hide-details>
                <v-radio
                    label="なし"
                    value="none">
                </v-radio>
              </v-radio-group>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="2">
              <v-radio-group
                  v-model="calendarView.endType"
                  row
                  hide-details>
                <v-radio
                    label="終了日"
                    value="endDate">
                </v-radio>
              </v-radio-group>
            </v-col>
            <v-col cols="10" class="el-form-col">
              <el-date-picker
                  v-model="calendarView.endDate"
                  type="datetime"
                  placeholder="終了日時"
                  :readonly="calendarView.endType !== 'endDate'"
                  :clearable="false">
              </el-date-picker>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="2">
              <v-radio-group
                  v-model="calendarView.endType"
                  row
                  hide-details>
                <v-radio
                    label="回数"
                    value="numberOfOccurrences">
                </v-radio>
              </v-radio-group>
            </v-col>
            <v-col cols="10" class="form-col">
              <v-text-field
                  v-model="calendarView.numberOfOccurrences"
                  :rules="[
                      v => !!v || v === 0 || '数値を入力してください',
                      v => !!/^-?[0-9]+(\.[0-9]*)?$/.test(v) || '数値を入力してください',
                      v => !/\./.test(v) || '整数を入力してください',
                      v => v <= 10000 || '10000 以下の値を入力してください',
                      v => v >= 0 || '0 以上の値を入力してください'
                    ]"
                  type="number"
                  min="1"
                  max="10000"
                  :readonly="calendarView.endType !== 'numberOfOccurrences'"
                  style="width: 120px;">
                <template #append>
                  <span class="ml-2 mt-1">回</span>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>

  </div>
</template>

<script>
import {
  CALENDAR_DAYS_OF_WEEK,
  CALENDAR_WEEKS,
  CALENDAR_INTERVAL_TYPES,
  IANA_TZ_IDS,
} from '@/constants'
import cloneDeep from "clone-deep";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import Formatter from "@/utils/Formatter";
import {DateTime, IANAZone} from "luxon";
import CalendarView, {createCalendarView} from "@/components/pages/implementation/TerminalImplementationList/CalendarView";

export default {
  name: 'CalendarForm',
  components: {TextFieldLike},
  mixins: [Formatter],
  model: {
    prop: 'calendar',
    event: 'change-form'
  },
  props: {
    calendar: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      calendarView: {},
      calendarViewOld: {},
    }
  },
  computed: {
    daysOfWeek: () => CALENDAR_DAYS_OF_WEEK,
    weeks: () => CALENDAR_WEEKS,
    intervalTypeItems() {
      return Object.entries(CALENDAR_INTERVAL_TYPES).map(([value, text]) => ({value, text}));
    },
    timeZones() {
      const now = Date.now();
      const result = {};
      for(const id of IANA_TZ_IDS) {
        const tz = IANAZone.create(id);
        const tzLong = tz.offsetName(now, {format: 'long', locale: navigator.language});
        const tzShort = tz.offsetName(now, {format: 'short', locale: navigator.language});
        result[id] = {
          text: `${id} ${tzLong} (${tzShort})`
        };
      }
      return result;
    },
    timeZoneItems() {
      return IANA_TZ_IDS.map(id => ({
        value: id,
        ...this.timeZones[id]
      }));
    },
    timeZoneString() {
      return this.timeZones[this.calendarView.recurrenceTimeZone]?.text;
    },
    intervalTypeDetailList() {
      return Object.entries(this.calendarView.intervalTypeDetails).map(([value, a]) => ({
        value,
        ...a,
        text: `毎月 ${a.text}`
      }));
    },
  },
  methods: {
    isSelectedDayOfWeek(dayOfWeek) {
      if(!this.calendarView.daysOfWeek) {
        return false;
      }
      return this.calendarView.daysOfWeek.includes(dayOfWeek);
    },
    toggleDayOfWeek(dayOfWeek) {
      if(!this.calendarView.daysOfWeek) {
        return;
      }
      const old = this.calendarView.daysOfWeek.slice();
      const pos = this.calendarView.daysOfWeek.indexOf(dayOfWeek);
      if(pos === -1) {
        old.push(dayOfWeek);
      } else {
        old.splice(pos, 1);
      }
      this.calendarView.daysOfWeek = old;
    },
    btnColorDayOfWeek(dayOfWeek) {
      return this.isSelectedDayOfWeek(dayOfWeek) ? 'primary text--white' : 'grey lighten-2 text--black';
    }
  },
  watch: {
    calendar: {
      immediate: true,
      deep: true,
      handler(value) {
        const cal = cloneDeep(value);
        if(!cal.startDate) {
          cal.startDate = DateTime.now().toISO({includeOffset: false});
        }
        if(!cal.recurrenceTimeZone) {
          cal.startDate = CalendarView.SYSTEM_TIMEZONE;
        }
        if((cal?.interval ?? 0) < 1) {
          cal.interval = 1;
        }
        if(!cal.endType) {
          if (cal.endDate) {
            cal.endType = 'endDate';
          } else if (cal.numberOfOccurrences) {
            cal.endType = 'numberOfOccurrences';
          } else {
            cal.endType = 'none';
          }
        }
        if((cal?.numberOfOccurrences ?? 0) < 1) {
          cal.numberOfOccurrences = 5;
        }
        if(!cal.endDate) {
          cal.endDate = DateTime
              .fromISO(cal.startDate, {zone: cal.recurrenceTimeZone})
              .plus({year: 5})
              .toISO({includeOffset: false});
        }
        this.calendarView = createCalendarView(cal);
        this.calendarViewOld = createCalendarView(cal);
        this.$emit('export', this.calendarView.export());
      }
    },
    calendarView: {
      deep: true,
      handler() {
        if(this.calendarView.equals(this.calendarViewOld)) {
          return;
        }
        this.$emit('change-form', this.calendarView.source);
        this.$emit('export', this.calendarView.export());
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.v-card.section {
  margin: 1rem 0;

  ::v-deep .v-card__title {
    color: rgba(0, 0, 0, 0.7);
    font-size: 16px;
    padding: 10px 20px 0 20px;

    .clear-btn {
      position: relative;
      top: -5px;
      left: 5px;
    }
  }
}

.el-form-col {
  display: flex;
  width: fit-content;
  flex-grow: initial;
  padding-top: 0.8rem;

  & > label {
    margin-right: 1rem;
    white-space: nowrap;
    align-self: center;
  }

  & > * {
    align-self: center;
  }

  ::v-deep .el-input__inner:not([readonly]) {
    border: 1px solid rgba(0,0,0,.42);
  }
}

.label-col {
  display: flex;
  align-items: center;
  padding-left: 0.5rem;
  & > label {
    vertical-align: middle;
  }
}

.form-col {
  display: flex;
  & > * {
    flex-grow: 0;
  }

  ::v-deep .v-input.v-input--is-readonly {
    color: gray !important;
    input {
      color: gray;
    }
  }
}
</style>
